import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, Route, Routes, useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {USER_TABS} from 'src/constants';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useSystemAccounts} from 'src/hooks/account/system-accounts';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {UserPageStructure} from 'src/modules/profile/user/components/user-page-structure';
import {clearUserProfileTab, selectUserInformation} from 'src/modules/profile/user/services/user-profile-slice';
import {
    LimitsRouter,
    ProductsTab,
    UserConferences,
    UserDetails,
    UserRoleSettings,
    UserSettings,
} from 'src/modules/profile/user/tabs';
import {AssignUserEntitlements} from 'src/modules/profile/user/tabs/assign-entilements';
import {AssignProductUserEntitlements} from 'src/modules/profile/user/tabs/assign-product-entilements';
import {EntitlementsWrapper} from 'src/modules/profile/user/tabs/entitlements';
import {UserActivityLog} from 'src/modules/profile/user/tabs/user-activity-log/user-activity-log';
import {getManagedAccounts} from 'src/openapi/admin/apis/GetManagedAccountsApi';
import {getUserInfo} from 'src/openapi/admin/apis/GetUserInfoApi';
import {getUserShortInfo} from 'src/openapi/admin/apis/GetUserShortInfoApi';
import {getUserAuthenticationInfo} from 'src/openapi/admin/apis/UserAuthenticationInfoApi';
import {UserStatus} from 'src/openapi/admin/models/UserStatus';
import {useAppDispatch} from 'src/store/hooks';

import Loading from '@pb/kit-component-loading';
import {useGateValue} from '@pb/kit-lib-statsig';

import {UserPageHeader} from './components';
import {clearUserPage, selectUserDetails, setIsAccountManager} from './services/user-page-slice';
import {AssignedAccounts} from './tabs/assigned-accounts';
import {UserAuditLog} from './tabs/audit-log/audit-log';
import {ContentRouter} from './tabs/content/content-router';
import {PersonalizationRouter} from './tabs/personalization';
import {UserLogin} from './tabs/user-login';

import type {Error} from 'src/openapi/admin/models/Error';


export const UserPage = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const queryParams = useParams<{userId: string}>();
    const userId = queryParams?.userId ? +queryParams.userId : -1;

    const {hasAccess, canEditUserEntitlements, canViewUserProducts} = useAdminAccessRight();
    const {isAdminAccount} = useSystemAccounts();
    const showContentTab = useMemo(() => hasAccess(ACCESS_RIGHTS.USER_CONTENT_VIEW), [hasAccess]);
    const isPumaGateEnabled = useGateValue(STATSIG_GATES.PUMA);

    const {shortInfo, isAccountManager, authInfo} = useSelector(selectUserDetails);
    const userDetails = useSelector(selectUserInformation);
    const information = useSelector(selectUserInformation);

    const clearUserPageData = useCallback(() => {
        dispatch(clearUserPage());
        dispatch(clearUserProfileTab());
    }, [dispatch]);

    useEffect(() => {
        clearUserPageData();

        const loadUserData = async () => {
            const userShortInfoResponse = await apiDispatch(getUserShortInfo({userId}));

            if (userShortInfoResponse.payload.error) {
                setLoadingError(userShortInfoResponse.payload.data);
            } else {
                const response = await apiDispatch(getManagedAccounts({accountManagerId: userId}));
                if (response.payload.success) {
                    dispatch(setIsAccountManager(response.payload.data.length > 0));
                }
            }
        };

        setIsLoading(true);
        loadUserData();
        setIsLoading(false);
    }, [userId, apiDispatch, clearUserPageData, dispatch]);

    useEffect(() => {
        const isUserActive = userDetails?.statusCode === UserStatus.ENABLED;
        const isConferenceAttendee = userDetails?.virtualAccounts?.length;
        apiDispatch(getUserAuthenticationInfo({userId: +userId}));

        if (isUserActive || isConferenceAttendee) {
            const pollUserAuthInfo = () => {
                apiDispatch(getUserAuthenticationInfo({userId: +userId}));
            };

            const intervalId = setInterval(pollUserAuthInfo, 120000);
            return () => clearInterval(intervalId);
        }
    }, [userId, apiDispatch, userDetails]);

    useEffect(() => {
        return clearUserPageData;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!information) {
            apiDispatch(getUserInfo({userId}));
        }
    }, [information, apiDispatch, userId]);

    const virtualAccounts = useMemo(() => userDetails?.virtualAccounts, [userDetails]);
    const hasVirtualAccounts = useMemo(() => (virtualAccounts?.length ?? 0) > 0, [virtualAccounts]);
    const isUserInformationLoading = useMemo(() => !loadingError && !userDetails, [loadingError, userDetails]);
    const accountId: number = useMemo(() => shortInfo?.accountId || 0, [shortInfo]);
    const userName = useMemo(() => shortInfo ? `${shortInfo.firstName} ${shortInfo.lastName}` : '', [shortInfo]);
    const canManageUserRole = useMemo(() => accountId &&
        isAdminAccount(accountId) && hasAccess(ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE,
    ), [accountId, isAdminAccount, hasAccess]);
    const canViewActivityLog = useMemo(() => accountId && isAdminAccount(accountId), [accountId, isAdminAccount]);
    const canViewUserLimits = useMemo(() => hasAccess(ACCESS_RIGHTS.USER_LIMITS_VIEW), [hasAccess]);
    const canViewUserEntitlements = useMemo(() => hasAccess(ACCESS_RIGHTS.USER_ENTITLEMENTS_VIEW), [hasAccess]);
    const conferencesContent = useMemo(() => {
        if (isUserInformationLoading) {
            return <Loading fast={true}/>;
        }

        if (hasVirtualAccounts) {
            return <UserConferences userId={userId}/>;
        }

        return (
            <Navigate
                to={`/profile/user/${userId}/${USER_TABS.DETAILS.name}`}
                replace={true}
            />
        );
    }, [hasVirtualAccounts, isUserInformationLoading, userId]);

    const canViewPuma = useMemo(() =>
        !!accountId && canViewUserProducts(accountId), [accountId, canViewUserProducts]);

    const isPumaAvailable = useMemo(() => isPumaGateEnabled
        && canViewPuma, [canViewPuma, isPumaGateEnabled]);

    return (
        <Skeleton shown={isLoading || !accountId} view={<Loading fast={true}/>}>
            <ServerResponseHandler serverError={loadingError} sectionName="User Profile">
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    <UserPageHeader
                        authInfo={authInfo}
                        shortInfo={shortInfo}
                        onChangeUser={() => apiDispatch(getUserShortInfo({userId}))}
                    />
                    <Routes>
                        <Route
                            path={`/${USER_TABS.DETAILS.name}`}
                            element={(
                                <UserPageStructure
                                    userId={userId}
                                    activeTab={USER_TABS.DETAILS.name}
                                    showSidebar={true}>
                                    <UserDetails key={accountId} userId={userId} accountId={accountId}/>
                                </UserPageStructure>
                            )}
                        />

                        <Route
                            path={`/${USER_TABS.SETTINGS.name}`}
                            element={(
                                <UserPageStructure
                                    userId={userId}
                                    activeTab={USER_TABS.SETTINGS.name}
                                    showSidebar={true}>
                                    <UserSettings userId={userId} accountId={accountId}/>
                                </UserPageStructure>
                            )}
                        />

                        <Route
                            path={`/${USER_TABS.LOGIN.name}`}
                            element={(
                                <UserPageStructure
                                    userId={userId}
                                    activeTab={USER_TABS.LOGIN.name}
                                    showSidebar={true}>
                                    <UserLogin
                                        userId={userId}
                                        accountId={accountId}
                                    />
                                </UserPageStructure>
                            )}
                        />
                        {isPumaAvailable ? (
                            <Route
                                path={`/${USER_TABS.PRODUCTS.name}`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.PRODUCTS.name}
                                        canViewUserProducts={isPumaAvailable}>
                                        <ProductsTab userId={userId} accountId={accountId} userName={userName}/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}
                        {canViewUserLimits ? (
                            <Route
                                path={`/${USER_TABS.LIMITS.name}/*`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.LIMITS.name}
                                        showSidebar={true}>
                                        <LimitsRouter userId={userId} accountId={accountId}/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        <Route
                            path={`/${USER_TABS.PERSONALIZATION.name}/*`}
                            element={(
                                <UserPageStructure
                                    userId={userId}
                                    activeTab={USER_TABS.PERSONALIZATION.name}
                                    showSidebar={true}>
                                    <PersonalizationRouter userId={userId} accountId={accountId}/>
                                </UserPageStructure>
                            )}
                        />

                        {canManageUserRole ? (
                            <Route
                                path={`/${USER_TABS.ROLE_SETTINGS.name}/*`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.ROLE_SETTINGS.name}
                                        showSidebar={true}>
                                        <UserRoleSettings userId={userId} accountId={accountId}/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        <Route
                            path={`/${USER_TABS.CONFERENCES.name}`}
                            element={(
                                <UserPageStructure
                                    userId={userId}
                                    activeTab={USER_TABS.CONFERENCES.name}>
                                    {conferencesContent}
                                </UserPageStructure>
                            )}
                        />

                        {(isAccountManager && !!shortInfo) ? (
                            <Route
                                path={`/${USER_TABS.ASSIGNED_ACCOUNTS.name}`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.ASSIGNED_ACCOUNTS.name}>
                                        <AssignedAccounts user={shortInfo} place="Assigned Accounts"/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        {canViewActivityLog ? (
                            <Route
                                path={`/${USER_TABS.ACTIVITY_LOG.name}`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.ACTIVITY_LOG.name}
                                        showSidebar={true}>
                                        <UserActivityLog userId={userId}/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}
                        <Route
                            path={`/${USER_TABS.AUDIT_LOG.name}`}
                            element={(
                                <UserPageStructure
                                    userId={userId}
                                    activeTab={USER_TABS.AUDIT_LOG.name}>
                                    <UserAuditLog userId={userId}/>
                                </UserPageStructure>
                            )}
                        />
                        {canViewUserEntitlements ? (
                            <Route
                                path={`/${USER_TABS.ENTITLEMENTS.name}/*`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.ENTITLEMENTS.name}>
                                        <EntitlementsWrapper userId={userId} accountId={accountId}/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        {canEditUserEntitlements(accountId) ? (
                            <Route
                                path={`/${USER_TABS.ASSIGN_ENTITLEMENTS.name}`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.ASSIGN_ENTITLEMENTS.name}>
                                        <AssignUserEntitlements/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        {canEditUserEntitlements(accountId) ? (
                            <Route
                                path="/assign-product-resources"
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.ASSIGN_ENTITLEMENTS.name}>
                                        <AssignProductUserEntitlements/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        {showContentTab ? (
                            <Route
                                path={`/${USER_TABS.CONTENT.name}/*`}
                                element={(
                                    <UserPageStructure
                                        userId={userId}
                                        activeTab={USER_TABS.CONTENT.name}>
                                        <ContentRouter userId={userId}/>
                                    </UserPageStructure>
                                )}
                            />
                        ) : null}

                        <Route
                            path="*"
                            element={
                                <Navigate to={`/profile/user/${userId}/${USER_TABS.DETAILS.name}`} replace={true}/>
                            }
                        />
                    </Routes>
                </ErrorBoundary>
            </ServerResponseHandler>
        </Skeleton>
    );
};
